<template>
  <div class="nebMultiSelect"
    v-neb-click-outside="onClickOutside"
  >
    <div class="nebMultiSelect-select"
      :class="{
        placeholder: !valueSelected,
        disabled,
        readOnly,
        open: showDropdown,
        dirty: highlightDirty && !isEqual(defaultValue, value)
      }"
      @click="toggleShowDropdown"
      ref="select"
    >
      <span>{{ valueLabel }}</span>

      <neb-icon name="caretDown"
        height="12px"
        width="12px"
        v-show="clearable ? !readOnly && (!valueSelected || isEqual(value, defaultValue)) : !readOnly"
      ></neb-icon>

      <neb-icon name="x"
        height="12px"
        width="12px"
        v-show="!readOnly && !disabled && clearable && valueSelected && !isEqual(value, defaultValue)"
        @click.native="onClear"
      ></neb-icon>
    </div>

    <div class="nebMultiSelect-dropdown"
      v-show="showDropdown"
    >
      <div class="nebMultiSelect-dropdown-search"
        v-if="searchable"
      >
        <neb-input v-model="formData.keyword"
          placeholder="Search"
          icon="search"
        ></neb-input>
      </div>

      <div class="nebMultiSelect-dropdown-clearSelected"
        v-show="valueSelected"
      >
        <span @click="onClearClick">Clear Selected</span>
      </div>

      <div class="nebMultiSelect-dropdown-optionSection selected"
        v-show="valueSelected"
        :class="{ overflow: selectedOverflowing }"
      >
        <div class="wrapper"
          @scroll="onSelectedWapperScroll"
        >
          <div class="nebMultiSelect-dropdown-option"
            v-for="opt in selectedOptions"
            :key="opt.value"
            @click="onSelect(opt)"
          >
            <neb-checkbox :value="true"
              :label="opt.label"
            ></neb-checkbox>
          </div>
        </div>
      </div>

      <hr class="divider"
        v-show="valueSelected"
      >

      <div class="nebMultiSelect-dropdown-optionSection notSelected"
        :class="{ overflow: notSelectedOverflowing }"
      >
        <div class="wrapper"
          @scroll="onNotSelectedWapperScroll"
        >
          <div class="nebMultiSelect-dropdown-option"
            v-for="opt in notSelectedOptions"
            :key="opt.value"
            @click="onSelect(opt)"
          >
            <neb-checkbox :value="false"
              :label="opt.label"
            ></neb-checkbox>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { includes, lowerCase, isEqual } from 'lodash'

import '../../directives/clickOutside'

import NebIcon from '../../Icon'
import NebCheckbox from '../Checkbox'
import NebInput from '../Input'

export default {
  name: 'NebMultiSelect',

  components: {
    NebIcon,
    NebCheckbox,
    NebInput
  },

  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Select Value'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      deefault: false
    },
    prefix: {
      type: String,
      default: ''
    },
    highlightDirty: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: Array
    },
    clearable: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    showDropdown: false,
    selectedOverflowing: false,
    notSelectedOverflowing: false,
    formData: {
      keyword: ''
    },
    show: 0,
    hide: 0
  }),

  computed: {
    selectedOptions () {
      return this.options
        .filter(opt => includes(this.value, opt.value))
    },
    notSelectedOptions () {
      const options = this.options
        .filter(opt => !includes(this.value, opt.value))

      if (this.formData.keyword !== '') {
        return options
          .filter(opt => includes(lowerCase(opt.label), lowerCase(this.formData.keyword)))
      } else {
        return options
      }
    },
    valueLabel () {
      let label = this.placeholder

      const { show, hide } = this.$data

      if (this.valueSelected) {
        label = this.selectedOptions
          .map(opt => opt.label)
          .slice(0, show)
          .join(', ')
      }

      if (hide > 0) {
        return this.prefix
          ? `${this.prefix}: ${label} + ${hide} more`
          : `${label} + ${hide} more`
      } else {
        return this.prefix ? `${this.prefix}: ${label}` : label
      }
    },
    valueSelected () {
      return this.selectedOptions.length > 0
    }
  },

  methods: {
    isEqual: isEqual,
    onClear (e) {
      e.stopPropagation()
      e.preventDefault()

      this.$emit('clear')
    },
    setOptionCounts () {
      const selectedOptions = this.selectedOptions
      if (!this.$refs.select) { return 0 }

      const selectDimensions = this.$refs.select.getBoundingClientRect()
      const maxWidth = selectDimensions.width - 110

      const wrapper = document.createElement('span')
      wrapper.style.visibility = 'hidden'

      document.body.appendChild(wrapper)

      let showCounter = 0
      let hideCounter = 0

      if (this.prefix) {
        wrapper.appendChild(
          document.createTextNode(this.prefix)
        )
      }

      selectedOptions
        .map(opt => opt.label)
        .forEach(v => {
          const newNode = document.createTextNode(v)
          wrapper.appendChild(newNode)

          if (wrapper.getBoundingClientRect().width < maxWidth) {
            showCounter += 1
          } else {
            hideCounter += 1
          }
        })

      document.body.removeChild(wrapper)

      this.show = showCounter
      this.hide = hideCounter
    },
    toggleShowDropdown () {
      if (this.disabled || this.readOnly) { return }
      this.showDropdown = !this.showDropdown
    },
    onSelect (opt) {
      let newValue

      if (includes(this.value, opt.value)) {
        newValue = this.value
          .filter(v => v !== opt.value)
      } else {
        newValue = this.value
          .concat([opt.value])
      }

      this.$emit('input', newValue)
    },
    onClickOutside () {
      if (this.showDropdown) {
        this.showDropdown = false
      }
    },
    onClearClick () {
      this.$emit('input', [])
    },
    updateOverlays () {
      // Add the overflow class to the option sections so we can apply a gradient.
      const selectedEl = document.querySelector('.nebMultiSelect-dropdown-optionSection.selected>.wrapper')
      const notSelectedEl = document.querySelector('.nebMultiSelect-dropdown-optionSection.notSelected>.wrapper')

      if (!selectedEl) {
        this.selectedOverflowing = false
      } else {
        this.selectedOverflowing = (selectedEl.clientHeight < selectedEl.scrollHeight)
      }

      if (!notSelectedEl) {
        this.notSelectedOverflowing = false
      } else {
        this.notSelectedOverflowing = notSelectedEl.clientHeight < notSelectedEl.scrollHeight
      }
    },
    onSelectedWapperScroll (e) {
      this.selectedOverflowing = !(e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight)
    },
    onNotSelectedWapperScroll (e) {
      this.notSelectedOverflowing = !(e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight)
    }
  },

  watch: {
    showDropdown () {
      this.$nextTick(this.updateOverlays)
    },
    value () {
      this.$nextTick(this.updateOverlays)
      this.setOptionCounts()
    }
  },

  mounted () {
    this.updateOverlays()
    this.setOptionCounts()

    window.addEventListener('resize', this.setOptionCounts)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.setOptionCounts)
  }
}
</script>

<style lang="scss" scoped>
@import '../../style.scss';

.nebMultiSelect {
  height: 34px;
  position: relative;
  width: 100%;

  &-select {
    height: 34px;
    background-color: $white;
    border-radius: 3px;
    border: solid 1px $light-grey-blue;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-left: 15px;
    padding-right: 15px;
    padding-top: 1px;
    color: $slate-grey;
    font-family: $defaultFont;
    font-size: 12px;
    cursor: pointer;
    position: relative;

    >span {
      margin-right: 10px;
    }

    &.dirty {
      background-color: #e8f4fc;
      border: 1px solid #a0c3d2;
    }

    .nebIcon {
      color: $slate-grey;
      opacity: 0.7;
    }

    &.placeholder {
      color: $warm-grey;
    }

    &.disabled {
      color: $pinkish-grey;
      background-color: $white-four;
      cursor: not-allowed;

      .nebIcon {
        color: $warm-grey;
      }
    }

    &.readOnly {
      border-top: none;
      border-left: none;
      border-right: none;
      border-bottom: 2px solid $warm-grey;
      cursor: initial;
      border-radius: initial;
    }

    &.disabled.readOnly {
      background-color: $white;
    }

    &.open {
      box-shadow: 0 0 3px 0 $tiffany-blue;
      border: solid 1px $tiffany-blue;
    }
  }

  &-dropdown {
    background-color: $white;
    border-radius: 3px;
    border: solid 1px $pinkish-grey;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 39px;
    left: 0;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    box-sizing: border-box;
    overflow: hidden;
    z-index: 2001;

    &-option {
      cursor: pointer;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      box-sizing: border-box;
      padding: 9px 15px;

      &:hover {
        background-color: $duck-egg-blue;
      }
    }

    &-optionSection {
      box-sizing: border-box;
      overflow: hidden;
      width: 100%;
      max-height: 40vh;
      position: relative;

      .wrapper {
        overflow-x: hidden;
        overflow-y: scroll;
        display: flex;
        justify-content: flex-start;
        flex-direction: column;
        align-items: stretch;
        max-height: 40vh;
        width: 100%;
        box-sizing: border-box;
      }

      &.overflow {
        &::after {
          content: "";
          box-shadow: 0 -50px 50px -20px white inset;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }
      }
    }

    &-clearSelected {
      box-sizing: border-box;
      overflow: hidden;
      width: 100%;
      padding-top: 10px;
      padding-left: 15px;

      span {
        font-size: 10px;
        cursor: pointer;
        font-family: $defaultFont;
        color: $tiffany-blue;
      }
    }

    &-search {
      padding: 10px;
    }
  }
}
</style>
